import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, TrendingUp, AlertTriangle, MessageSquare, Lightbulb, RefreshCw, Send, BarChart3 } from 'lucide-react';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils-helpers';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function AdminAIAnalytics() {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);

  useEffect(() => {
    loadAllAIData();
  }, []);

  const loadAllAIData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadInsights(),
        loadForecast(),
        loadAnomalies(),
        loadRecommendations()
      ]);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load AI analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async () => {
    try {
      const res = await api.get('/ai/dashboard-insights');
      setInsights(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadForecast = async () => {
    try {
      const res = await api.get('/ai/revenue-forecast?days=30');
      setForecast(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAnomalies = async () => {
    try {
      const res = await api.get('/ai/anomaly-detection');
      setAnomalies(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadRecommendations = async () => {
    try {
      const res = await api.get('/ai/recommendations');
      setRecommendations(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNaturalQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setQueryLoading(true);
    try {
      const res = await api.post('/ai/natural-query', { query, context_type: 'general' });
      setQueryResult(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to process query');
    } finally {
      setQueryLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" data-font="display">
            <Sparkles className="h-8 w-8 text-primary" />
            AI-Powered Analytics
          </h1>
          <p className="text-muted-foreground mt-1">Advanced insights, forecasting, and recommendations powered by GPT-5.1</p>
        </div>
        <Button onClick={loadAllAIData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh All
        </Button>
      </div>

      {insights && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-2xl font-bold">{insights.analytics.total_orders}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(insights.analytics.revenue)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Vendors</p>
                  <p className="text-2xl font-bold">{insights.analytics.active_vendors}</p>
                </div>
                <Sparkles className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Online Drivers</p>
                  <p className="text-2xl font-bold">{insights.analytics.online_drivers}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="insights" className="w-full">
        <TabsList>
          <TabsTrigger value="insights"><Sparkles className="h-4 w-4 mr-2" />AI Insights</TabsTrigger>
          <TabsTrigger value="forecast"><TrendingUp className="h-4 w-4 mr-2" />Revenue Forecast</TabsTrigger>
          <TabsTrigger value="anomalies"><AlertTriangle className="h-4 w-4 mr-2" />Anomalies</TabsTrigger>
          <TabsTrigger value="recommendations"><Lightbulb className="h-4 w-4 mr-2" />Recommendations</TabsTrigger>
          <TabsTrigger value="query"><MessageSquare className="h-4 w-4 mr-2" />Ask AI</TabsTrigger>
        </TabsList>

        <TabsContent value="insights" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Dashboard Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights ? (
                <div className="space-y-4">
                  <div className="p-4 bg-primary/5 border-l-4 border-primary rounded-lg">
                    <p className="text-sm leading-relaxed whitespace-pre-line">{insights.ai_insights.insights}</p>
                    <p className="text-xs text-muted-foreground mt-3">
                      Generated {formatDate(insights.ai_insights.generated_at)}
                    </p>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Order Status Distribution</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(insights.analytics.order_status).map(([status, count]) => (
                        <div key={status} className="p-3 border rounded-lg">
                          <p className="text-xs text-muted-foreground capitalize">{status.replace('_', ' ')}</p>
                          <p className="text-xl font-bold">{count}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Loading insights...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="forecast" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                AI Revenue Forecasting
              </CardTitle>
            </CardHeader>
            <CardContent>
              {forecast ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['7_day', '14_day', '30_day'].map((period, idx) => {
                      const colors = ['green', 'blue', 'purple'];
                      const data = forecast.forecast.forecasts[period] || {};
                      return (
                        <Card key={period} className={`border-${colors[idx]}-200`}>
                          <CardContent className="p-6">
                            <p className="text-sm text-muted-foreground">{period.replace('_', '-').toUpperCase()} Forecast</p>
                            <p className={`text-2xl font-bold text-${colors[idx]}-600`}>
                              {formatCurrency(data.revenue || 0)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">~{data.orders || 0} orders</p>
                            <Badge className="mt-2">{data.confidence || 'medium'} confidence</Badge>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Historical Revenue Trend</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={forecast.historical_data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Line type="monotone" dataKey="revenue" stroke="hsl(351 96% 31%)" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Loading forecast...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="anomalies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                Anomaly Detection (Last 24 Hours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {anomalies && anomalies.anomalies?.anomalies?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No anomalies detected - All systems normal</p>
                </div>
              ) : anomalies?.anomalies?.anomalies ? (
                anomalies.anomalies.anomalies.map((anomaly, idx) => (
                  <div key={idx} className="p-4 border rounded-lg mb-3">
                    <Badge className={getSeverityColor(anomaly.severity)}>
                      {anomaly.severity?.toUpperCase()}
                    </Badge>
                    <p className="font-medium mt-2">{anomaly.type?.replace('_', ' ')}</p>
                    <p className="text-sm text-muted-foreground">{anomaly.description}</p>
                    {anomaly.recommendation && (
                      <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                        <p className="text-sm font-medium">Recommendation:</p>
                        <p className="text-sm mt-1">{anomaly.recommendation}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">Loading...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-600" />
                Strategic Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recommendations ? (
                <div className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-lg">
                  <Lightbulb className="h-6 w-6 text-yellow-600 mb-3" />
                  <p className="text-sm leading-relaxed whitespace-pre-line">{recommendations.recommendations.recommendations}</p>
                  <p className="text-xs text-muted-foreground mt-4">
                    Generated {formatDate(recommendations.recommendations.generated_at)}
                  </p>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Loading recommendations...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="query" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Ask AI Anything
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleNaturalQuery} className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Ask a question (e.g., 'What are the top 5 vendors this month?')"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={queryLoading}
                  />
                  <Button type="submit" disabled={queryLoading || !query.trim()}>
                    {queryLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>

                {queryResult && (
                  <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                    <p className="text-sm font-medium mb-2">Q: {queryResult.query}</p>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{queryResult.answer}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium mb-2">Example Questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {['Show me top 5 vendors by revenue', 'What is the average delivery time?', 'How many orders were cancelled today?'].map(ex => (
                      <Button key={ex} variant="outline" size="sm" onClick={() => setQuery(ex)} type="button">
                        {ex}
                      </Button>
                    ))}
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
